import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  PLATFORM_ID,
  ViewChild,
  inject,
} from '@angular/core';
import { PlatformLocation, isPlatformBrowser } from '@angular/common';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationStart,
  Router,
} from '@angular/router';
import { FooterComponent } from './footer.component';
import { SharedService } from './shared.service';
import { Utils } from './utils';
import { filter, map, switchMap } from 'rxjs/operators';
import { Title } from '@angular/platform-browser';
import { SeoService } from './services/seo.service';
import { BreadcrumbsService } from './services/breadcrumbs.service';
import { environment } from 'client/environments/environment';
import { AnalyticsService } from './services/analytics.service';
import { ToastService } from './admin/services/toast.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, AfterViewInit {
  @ViewChild(FooterComponent) footerRef: FooterComponent;
  @ViewChild('transitionRef') transitionRef: ElementRef;

  public menuItems: string[] = [
    'Voor professionals',
    'Voor opdrachtgevers',
    'Stories',
    'Over Blauwtand',
    'Werken bij',
    'Contact',
  ];
  public isMobileMenuHidden = true;
  public isBrowserRefreshed = false;
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  // Server renders read the request path from the platform location; browser behavior is unchanged.
  public isAdminRoute = inject(PlatformLocation).pathname.startsWith('/admin');
  public redirectTo: (name: string) => void = Utils.redirectTo;
  private scrollY = 0; // Track scroll position for iOS

  public get hasCookieConsent() {
    const cookie = this.cookiesToMap()?.get('cookie_consent');
    return cookie === 'true' || cookie === 'false';
  }

  public constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly titleService: Title,
    private readonly seo: SeoService,
    private readonly breadcrumbsService: BreadcrumbsService,
    public readonly sharedService: SharedService,
    private readonly analytics: AnalyticsService,
    private readonly toastService: ToastService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.handleAuthErrorToast();

    const base = this.isBrowser
      ? window.location.origin
      : environment.siteUrl?.replace(/\/$/, '') || '';
    this.seo.addJsonLd('org', {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Blauwtand',
      url: base,
      logo: `${base}/assets/blauwtand.svg`,
      sameAs: [],
    });
    this.seo.addJsonLd('website', {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Blauwtand',
      url: base,
    });

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        this.isAdminRoute = e.urlAfterRedirects.startsWith('/admin');
        this.cdr.markForCheck();
      });

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        map(() => {
          let r = this.activatedRoute as any;
          while (r.firstChild) r = r.firstChild;
          return r;
        }),
        switchMap((r: any) => r.data)
      )
      .subscribe((data: any) => {
        // BLA-54: SEO pages own their title, canonical, robots and JSON-LD; the shared route
        // metadata must never overwrite them after the page applied its resolved payload.
        if (data?.['seoOwner']) return;
        this.seo.updateSeo({
          title: data?.['title'],
          description: data?.['description'],
          robots: data?.['robots'],
          canonicalUrl: data?.['canonicalUrl'],
          imageUrl: data?.['imageUrl'],
        });

        // Page view tracking moved to AnalyticsService (subscription established there)
      });

    // Breadcrumbs JSON-LD driven by computed breadcrumbs service
    const baseUrl =
      environment.siteUrl?.replace(/\/$/, '') ||
      (this.isBrowser ? window.location.origin : '');
    this.breadcrumbsService.trail$.subscribe((trail) => {
      const crumbs = trail.items;
      // A complete (SEO) trail's BreadcrumbList is emitted once by the SEO page itself.
      if (!trail.complete && crumbs && crumbs.length) {
        const itemListElement = crumbs.map((c, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: c.label,
          ...(c.url ? { item: `${baseUrl}${c.url}` } : {}),
        }));
        this.seo.addJsonLd('breadcrumbs', {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement,
        });
      } else {
        this.seo.removeJsonLd('breadcrumbs');
      }
    });
  }

  public async ngAfterViewInit(): Promise<void> {
    // No browser listeners during a server render.
    if (!this.isBrowser) return;
    await Utils.timeout();
    window.addEventListener('resize', () => {
      if (window.innerWidth >= 1024) {
        this.isMobileMenuHidden = true;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, this.scrollY);
        this.cdr.markForCheck();
      }
    });
  }

  public toggleMobileMenu(): void {
    this.isMobileMenuHidden = !this.isMobileMenuHidden;
    
    // Prevent body scroll when mobile menu is open
    // iOS-specific handling to prevent bounce/scroll
    if (this.isMobileMenuHidden) {
      // Restore scroll position
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo(0, this.scrollY);
    } else {
      // Store current scroll position
      this.scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    }
  }

  public navigateTo(menuItem: string, isMobileMenu: boolean = false): void {
    if (menuItem) {
      switch (menuItem) {
        case 'Over Blauwtand':
          menuItem = 'over-blauwtand';
          break;
        case 'Voor opdrachtgevers':
          menuItem = 'opdrachtgevers';
          break;
        case 'Voor professionals':
          menuItem = 'vacatures';
          break;
        case 'Stories':
          menuItem = 'stories';
          break;
        case 'Werken bij':
          menuItem = 'werken-bij';
          break;
      }

      if (
        this.router.url.includes(menuItem.toLowerCase()) ||
        (this.router.url == '/' && menuItem == 'home')
      ) {
        this.router.navigate([`${menuItem.toLowerCase()}`], {
          relativeTo: this.activatedRoute,
        });
      } else {
        this.router.navigate([`${menuItem.toLowerCase()}`], {
          relativeTo: this.activatedRoute,
        });
      }
    }

    if (isMobileMenu) {
      this.toggleMobileMenu();
    }

    if (menuItem === 'home') {
      this.isMobileMenuHidden = true;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo(0, this.scrollY);
    }
  }

  public signIn(): void {
    const returnTo = `${window.location.origin}/admin`;
    window.location.href = `${environment.apiUrl}/auth/login?return_to=${encodeURIComponent(returnTo)}`;
  }

  public navigateHome() {
    if (this.router.url == '/') {
      this.router.navigate(['home']);
    } else {
      this.router.navigate(['home']);
    }
  }

  public navigatePrivacyStatement() {
    this.router.navigate(['privacy-statement']);
  }

  public cookiesReadMore() {
    window.open('https://www.cookiesandyou.com/');
  }

  public setCookieConsent(value: string) {
    document.cookie = `cookie_consent=${value};max-age=${60 * 60 * 24 * 365}`;
  }

  private handleAuthErrorToast() {
    const authError = this.activatedRoute.snapshot.queryParamMap.get('auth_error');
    if (!authError) {
      return;
    }

    if (authError === 'access_denied') {
      this.toastService.error('Inloggen mislukt. Je account heeft geen toegang.');
    } else {
      this.toastService.error('Inloggen mislukt. Probeer het opnieuw.');
    }

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { auth_error: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private cookiesToMap() {
    const cookiesArray = document.cookie.split(';').map((c) => c.split('='));
    const cookiesMap = new Map<string, string>();
    cookiesArray.forEach((i) => {
      cookiesMap.set(i?.[0]?.trim(), i?.[1]?.trim());
    });
    return cookiesMap;
  }
}
